import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { mouseoverRelativeCoordinatesObservable } from './mouseover-relative-coordinates-observable';

export interface ProgressTooltipContext {
  value: number;
}

@Directive({
  selector: '[bmMediaProgressTooltip]',
})
/**
 * Directive to attach
 */
export class MediaProgressTooltipDirective implements OnDestroy {
  @Input()
  public set bmMediaProgressTooltip(v: ElementRef | HTMLElement) {
    if (this._teardownListener) {
      this._teardownListener();
    }

    if (v) {
      this._bmMediaProgressTooltip = v;
      this._hostDOMEle = (v as ElementRef).nativeElement
        ? (v as ElementRef).nativeElement
        : (v as HTMLElement);

      this._teardownListener = this._render.listen(
        this._hostDOMEle,
        'mouseenter',
        this.onMouseenter.bind(this),
      );
    } else {
      this._hostDOMEle = null;
    }
  }
  public get bmMediaProgressTooltip() {
    return this._bmMediaProgressTooltip;
  }

  @Input() public value!: number;

  private _overlayRef!: OverlayRef | null;
  private _positionStategy!: FlexibleConnectedPositionStrategy | null;
  private _bmMediaProgressTooltip!: ElementRef | HTMLElement;
  private _templatePortal!: TemplatePortal<ProgressTooltipContext>;
  private _teardownListener!: () => void;
  private _hostDOMEle!: HTMLElement | null;

  constructor(
    private _templateRef: TemplateRef<ProgressTooltipContext>,
    private _viewContainerRef: ViewContainerRef,
    private _overlay: Overlay,
    // tslint:disable-next-line:no-any
    @Inject(DOCUMENT) private _document: any,
    private _render: Renderer2,
  ) {
    this._templatePortal = new TemplatePortal<ProgressTooltipContext>(
      this._templateRef,
      this._viewContainerRef,
    );
  }

  public ngOnDestroy() {
    this.tearDownPortals();
    if (this._teardownListener) {
      this._teardownListener();
    }
  }

  public onMouseenter($event: MouseEvent) {
    if (!this._hostDOMEle || this._templatePortal.isAttached) {
      return;
    }

    this.tearDownPortals();
    const { width } = this._hostDOMEle.getBoundingClientRect();

    this._positionStategy = this._overlay
      .position()
      .flexibleConnectedTo(this._hostDOMEle)
      .withPositions([
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ])
      .withDefaultOffsetY(-5);

    this._overlayRef = this._overlay.create({
      positionStrategy: this._positionStategy,
    });

    const { rootNodes } = this._overlayRef.attach(this._templatePortal);
    const getOffset = () => {
      if (rootNodes && rootNodes.length) {
        return (rootNodes[0] as HTMLElement).getBoundingClientRect().width / 2;
      }
      return 0;
    };

    mouseoverRelativeCoordinatesObservable($event, this._document).subscribe({
      next: val => {
        if (this._positionStategy) {
          this._positionStategy
            .withDefaultOffsetX(val * width - getOffset())
            .reapplyLastPosition();
        }
      },
      complete: () => {
        this.tearDownPortals();
      },
    });
  }

  private tearDownPortals() {
    if (this._templatePortal && this._templatePortal.isAttached) {
      this._templatePortal.detach();
    }

    if (this._overlayRef) {
      this._overlayRef.detach();
      this._overlayRef.dispose();
    }

    if (this._positionStategy) {
      this._positionStategy.dispose();
    }
  }
}
